import { Button } from "@/components/ui/button";
import { cn, getBackToDashboardLink, getUserType } from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, useLocation } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import type { ReactNode } from "react";

/** When set from the license payment screen, “Back to dashboard” reopens details with Property in view. */
export interface RegistrantDashboardBackSearch {
  openLicense: string;
  /** Defaults to true when omitted. */
  focusProperty?: boolean;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  subtitleElement?: ReactNode;
  leadingAction?: ReactNode;
  action?: ReactNode;
  className?: string;
  registrantDashboardBackSearch?: RegistrantDashboardBackSearch;
  /** When true, omits the back link (e.g. when shown in SetupPageTabBar above the title). */
  hideBackButton?: boolean;
  /** Returns to the previous page instead of the dashboard link. */
  onBack?: () => void;
}

export default function PageHeader({
  title,
  subtitle,
  subtitleElement,
  leadingAction,
  action,
  className,
  registrantDashboardBackSearch,
  hideBackButton = false,
  onBack,
}: PageHeaderProps) {
  const { user } = useAuth0();
  const location = useLocation();
  const showBackButton =
    !hideBackButton &&
    location.pathname !== "/registrant/dashboard" &&
    location.pathname !== "/operator/overview" &&
    location.pathname !== "/government/overview" &&
    !location.pathname.startsWith("/account-setup");

  const userType = getUserType(user);
  if (!userType) {
    console.error("PageHeader loaded without a user type");
    return null;
  }

  const backDashboardLink = getBackToDashboardLink(userType, location.pathname);
  const registrantDashboardBack =
    registrantDashboardBackSearch &&
    backDashboardLink === "/registrant/dashboard"
      ? {
          to: "/registrant/dashboard" as const,
          search: {
            openLicense: registrantDashboardBackSearch.openLicense,
            focusProperty:
              registrantDashboardBackSearch.focusProperty !== false,
          },
        }
      : null;

  return (
    <div
      className={cn(
        "flex w-full self-start flex-col md:flex-row md:items-center justify-between gap-4",
        className,
      )}
    >
      <div className="flex flex-col gap-2">
        {showBackButton && onBack ? (
          <Button
            variant="link"
            size="sm"
            type="button"
            className="has-[>svg]:pl-0 w-fit"
            onClick={onBack}
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Back</span>
          </Button>
        ) : null}
        {showBackButton && !onBack && (
          <Button
            variant="link"
            size="sm"
            asChild
            className="has-[>svg]:pl-0 w-fit"
          >
            {registrantDashboardBack ? (
              <Link
                to={registrantDashboardBack.to}
                search={registrantDashboardBack.search}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Back to dashboard</span>
              </Link>
            ) : (
              <Link to={backDashboardLink} className="flex items-center gap-1">
                <ChevronLeft className="h-4 w-4" />
                <span>Back to dashboard</span>
              </Link>
            )}
          </Button>
        )}
        <div>
          <div className="flex items-center gap-2">
            {leadingAction}
            <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
          </div>
          {subtitleElement ? (
            <div className="flex items-center gap-2">
              {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
              {subtitleElement}
            </div>
          ) : (
            subtitle && <p className="text-muted-foreground">{subtitle}</p>
          )}
        </div>
      </div>
      {action && action}
    </div>
  );
}
